/**
 * Dashboard — "Warm Confident" variant (Direction A). Shown in LIGHT mode.
 *
 * Warm paper neutral, ochre accent, Bricolage Grotesque display over Hanken
 * Grotesk body. A circular score ring hero with encouraging copy, rounded
 * tiles, and a framework list with soft status chips + progress tracks.
 *
 * Presentational only — all data arrives as a prop from the page shell. The
 * look is driven entirely by scoped CSS in dashboard-theme.css; this wrapper
 * (.oc-dash-warm) is hidden by CSS in dark mode.
 *
 * COLOUR: the ochre accent is IDENTITY (ring, tracks, tiles) — it never means
 * "good/bad". Discrete statuses (Reopened/Draft/…) keep the semantic ramp via
 * soft chips. Framework identity uses the categorical FrameworkMark, not ochre.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TONE_FOR_STATUS, type Tone } from '../../components/ui';
import { FrameworkMark } from '../../components/FrameworkMark';
import { initials, type DashboardVariantProps } from './types';

const RING_R = 50;
const RING_C = 2 * Math.PI * RING_R; // ~314.16

/** Map a semantic tone to a Warm soft-chip class (serious folds into warn). */
const CHIP_CLASS: Record<Tone, string> = {
  good: 'good', warning: 'warn', serious: 'warn', critical: 'crit', neutral: 'neutral',
};

export function DashboardWarm({ data }: DashboardVariantProps) {
  const { t, i18n } = useTranslation();

  const overall = data.overallCompliance;
  const notInPlan = data.gaps.byPlanMembership.NOT_IN_PLAN ?? 0;
  const ringPct = overall ?? 0;
  const dash = `${(ringPct / 100) * RING_C} ${RING_C}`;

  const today = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  const heroTitle = overall === null
    ? t('dashboard.warmHeroTitleStart', 'Let’s get your first framework assessed')
    : t('dashboard.warmHeroTitle', 'Good progress — now close the gaps');
  const heroBody = overall === null
    ? t('dashboard.warmHeroBodyStart', 'Pick a framework and answer its questions to see where you stand.')
    : t('dashboard.warmHeroBody', {
        gaps: data.gaps.total,
        notInPlan,
        defaultValue: 'You have {{gaps}} open gaps, {{notInPlan}} not yet in a plan. Put them into a plan to move the needle.',
      });

  return (
    <div className="oc-dash-warm">
      {/* Header: date + org, with an org identity mark */}
      <div className="wtop">
        <div>
          <div className="date">{today}</div>
          <div className="org">
            <span className="sr-only">{t('dashboard.title')}: </span>
            {data.organization.name}
          </div>
        </div>
        <div className="orgmark" aria-hidden>{initials(data.organization.name)}</div>
      </div>

      {/* Hero: score ring + encouraging copy */}
      <div className="hero">
        <div className="ring">
          <svg viewBox="0 0 118 118" width="118" height="118" aria-hidden focusable="false">
            <circle cx="59" cy="59" r={RING_R} fill="none" stroke="#f0e6d6" strokeWidth="11" />
            <circle
              cx="59" cy="59" r={RING_R} fill="none" stroke="var(--accent)" strokeWidth="11"
              strokeLinecap="round" strokeDasharray={dash} transform="rotate(-90 59 59)"
            />
          </svg>
          <b>
            <span className="big tnum">{overall !== null ? `${overall}%` : '—'}</span>
            <span className="lab">
              {overall !== null ? t('dashboard.warmRingLabel', 'overall') : t('dashboard.notYetAssessed')}
            </span>
          </b>
        </div>
        <div>
          <h2>{heroTitle}</h2>
          <p>{heroBody}</p>
          <Link className="btn" to="/getting-started">
            {t('dashboard.warmCta', 'See what’s next')} <span aria-hidden>→</span>
          </Link>
        </div>
      </div>

      {/* Tiles: the headline numbers */}
      <div className="tiles">
        <Link className="tile" to="/gaps">
          <div className="k"><span className="dot" aria-hidden />{t('dashboard.openGaps')}</div>
          <div className="v tnum">{data.gaps.total}</div>
          <div className="h">{`${notInPlan} ${t('gaps.notInPlan').toLowerCase()}`}</div>
        </Link>
        <Link className="tile" to="/plans">
          <div className="k"><span className="dot" aria-hidden />{t('dashboard.openPlans')}</div>
          <div className="v tnum">{data.plans.open}</div>
          <div className="h">
            {data.plans.overdue > 0
              ? `${data.plans.overdue} ${t('dashboard.overduePlans').toLowerCase()}`
              : t('dashboard.warmNoneOverdue', 'none overdue')}
          </div>
        </Link>
        <Link className="tile" to="/policies">
          <div className="k"><span className="dot" aria-hidden />{t('dashboard.consolePolicies', 'Policies')}</div>
          <div className="v tnum">{data.policies.active}</div>
          <div className="h">{t('dashboard.consoleOfTotal', { total: data.policies.total, defaultValue: 'of {{total}} total' })}</div>
        </Link>
        <Link className="tile" to="/credits">
          <div className="k"><span className="dot" aria-hidden />{t('dashboard.credits')}</div>
          <div className="v tnum">{data.credits.balance}</div>
          <div className="h">{t('dashboard.warmCreditsHint', 'available')}</div>
        </Link>
      </div>

      {/* Frameworks */}
      <div className="fw">
        <h3>{t('dashboard.warmFrameworksTitle', 'Your frameworks')}</h3>
        {data.frameworks.length === 0 ? (
          <div className="frow" style={{ color: 'var(--mut)', fontWeight: 400 }}>
            {t('dashboard.noFrameworks')}
          </div>
        ) : (
          data.frameworks.map((fw) => {
            const href = fw.submissionId ? `/assessments/${fw.submissionId}` : '/assessments';
            const tone: Tone = fw.submissionStatus ? (TONE_FOR_STATUS[fw.submissionStatus] ?? 'neutral') : 'neutral';
            const statusLabel = fw.submissionStatus ? t(`status.${fw.submissionStatus}`) : t('status.NOT_STARTED');
            const rate = fw.complianceRate;
            return (
              <Link className="frow" to={href} key={fw.key}>
                <FrameworkMark
                  frameworkKey={fw.key}
                  size="sm"
                  className="!h-[34px] !w-[34px] !rounded-[11px]"
                />
                <span className="nm">
                  <span>{fw.name}</span>
                  <small>{fw.version}</small>
                </span>
                <span className={`chip ${CHIP_CLASS[tone]}`}>{statusLabel}</span>
                <span
                  className="track"
                  role="progressbar"
                  aria-valuenow={rate ?? 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${fw.name} ${t('dashboard.consoleColRate', 'Rate')}`}
                >
                  <i style={{ width: `${Math.max(0, Math.min(100, rate ?? 0))}%` }} />
                </span>
                <span className="pct tnum">{rate !== null ? `${rate}%` : '—'}</span>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
